
import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useLoader } from '../../../../context/LoaderProvider.jsx';

const Eventsettlementpage = () => {
    // Filter states
    const [searchType, setSearchType] = useState("vendorId"); // Default search type
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedStatusFilter, setSelectedStatusFilter] = useState("");
    const [selectedDate, setSelectedDate] = useState("");

    // Search options for dropdown
    const searchOptions = [
        { value: "vendorId", label: "Vendor ID" },
        { value: "holderName", label: "Vendor Name" },
        { value: "accountNumber", label: "Account Number" },
        { value: "ifscCode", label: "IFSC Code" }
    ];

    const [invoiceData, setInvoiceData] = useState(null);
    const [settlements, setSettlements] = useState([]);
    const [filteredSettlements, setFilteredSettlements] = useState([]);
    const [showStatusModal, setShowStatusModal] = useState(false);
    const [selectedSettlement, setSelectedSettlement] = useState(null);
    const [newStatus, setNewStatus] = useState("");
    const [remarks, setRemarks] = useState("");
    const [pytMode, setPytMode] = useState("FT");

    const { showLoader, hideLoader } = useLoader();

    // Fetch settlements from API
    useEffect(() => {
        fetchSettlements();
    }, []);

    const fetchSettlements = () => {
        const token = localStorage.getItem("authToken");
        showLoader();
        axios
            .get("https://backend.maamaas.com/food/api/settlements/get/all", {
                headers: {
                    Authorization: `Bearer ${token}`,
                    Accept: "*/*",
                },
            })
            .then((res) => {
                const data = res.data.settlements || res.data;
                // Add serial numbers
                const dataWithSno = Array.isArray(data) ?
                    data.map((item, index) => ({
                        ...item,
                        sno: index + 1
                    })) : [];

                setSettlements(dataWithSno);
                setFilteredSettlements(dataWithSno);
            })
            .catch((err) => console.error("Error fetching settlements:", err)).finally(() => {
                hideLoader();
            })
    };

    // Get placeholder text based on search type
    const getPlaceholderText = () => {
        switch (searchType) {
            case "vendorId": return "Search Vendor ID";
            case "holderName": return "Search Vendor Name";
            case "accountNumber": return "Search Account Number";
            case "ifscCode": return "Search IFSC Code";
            default: return "Search...";
        }
    };

    // Filter data based on selected search type, query, status, and date
    const searchFilteredData = useMemo(() => {
        if (!searchQuery.trim() && !selectedStatusFilter && !selectedDate) {
            return settlements;
        }

        const query = searchQuery.toLowerCase().trim();

        return settlements.filter(item => {
            // Search filter
            let searchMatch = true;
            if (searchQuery.trim()) {
                const fieldValue = item[searchType];
                searchMatch = fieldValue &&
                    fieldValue.toString().toLowerCase().includes(query);
            }

            // Status filter
            let statusMatch = true;
            if (selectedStatusFilter) {
                statusMatch = item.paymentStatus === selectedStatusFilter;
            }

            // Date filter
            let dateMatch = true;
            if (selectedDate) {
                const settlementDate = item.settlementDate || "";
                dateMatch = settlementDate === selectedDate;
            }

            return searchMatch && statusMatch && dateMatch;
        });
    }, [settlements, searchType, searchQuery, selectedStatusFilter, selectedDate]);

    // Update filteredSettlements whenever searchFilteredData changes
    useEffect(() => {
        setFilteredSettlements(searchFilteredData);
    }, [searchFilteredData]);

    // Clear all filters
    const clearAllFilters = () => {
        setSearchQuery("");
        setSelectedStatusFilter("");
        setSelectedDate("");
    };

    // Get unique statuses for filter dropdown
    const uniqueStatuses = useMemo(() => {
        const statuses = settlements.map(item => item.paymentStatus).filter(Boolean);
        return [...new Set(statuses)].sort();
    }, [settlements]);

    // Open status update modal
    const handleStatusUpdateClick = (settlement) => {
        setSelectedSettlement(settlement);
        setNewStatus(settlement.paymentStatus);
        setRemarks(settlement.remarks || "");
        setPytMode(settlement.pytMode || "FT");
        setShowStatusModal(true);
    };

    // Update settlement status
    const handleUpdateStatus = () => {
        if (!selectedSettlement || !newStatus) {
            alert("Please select a status");
            return;
        }

        if (!remarks.trim()) {
            alert("Please enter remarks for the status update");
            return;
        }

        const token = localStorage.getItem("authToken");
        const settlementId = selectedSettlement.settlementId;

        // Prepare update data
        const updateData = {
            settlementId: settlementId,
            vendorId: selectedSettlement.vendorId,
            totalCgst: selectedSettlement.totalCgst || 0,
            totalSgst: selectedSettlement.totalSgst || 0,
            totalGrandTotal: selectedSettlement.totalGrandTotal || 0,
            totalServiceCharges: selectedSettlement.totalServiceCharges || 0,
            totalPlatformCharges: selectedSettlement.totalPlatformCharges || 0,
            totalOnlinePayment: selectedSettlement.totalOnlinePayment || 0,
            totalAmountBeforeTds: selectedSettlement.totalAmountBeforeTds || 0,
            tdsAmount: selectedSettlement.tdsAmount || 0,
            amountAfterTds: selectedSettlement.amountAfterTds || 0,
            amountAfterCash: selectedSettlement.amountAfterCash || 0,
            finalAmount: selectedSettlement.finalAmount || 0,
            totalCash: selectedSettlement.totalCash || 0,
            description: selectedSettlement.description || "",
            fromDate: selectedSettlement.fromDate || new Date().toISOString(),
            toDate: selectedSettlement.toDate || new Date().toISOString(),
            totalVendorPlatformCharges: selectedSettlement.totalVendorPlatformCharges || 0,
            totalVendorPlatformChargesGst: selectedSettlement.totalVendorPlatformChargesGst || 0,
            holderName: selectedSettlement.holderName || "",
            accountNumber: selectedSettlement.accountNumber || "",
            ifscCode: selectedSettlement.ifscCode || "",
            settlementDate: selectedSettlement.settlementDate || new Date().toISOString().split('T')[0],
            paymentStatus: newStatus,
            superadminAccNo: selectedSettlement.superadminAccNo || "",
            pytMode: pytMode,
            remarks: remarks,
            totalWalletPayment: selectedSettlement.totalWalletPayment || 0,
            commission: selectedSettlement.commission || 0
        };

        showLoader();
        axios
            .put(`https://backend.maamaas.com/food/api/settlements/update/${settlementId}`, updateData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            })
            .then((res) => {
                alert("Status updated successfully!");

                // Refresh data from server to get updated data
                fetchSettlements();

                // Close modal
                setShowStatusModal(false);
                setSelectedSettlement(null);
                setNewStatus("");
                setRemarks("");
                setPytMode("FT");
            })
            .catch((err) => {
                console.error("Error updating status:", err);
                alert(`Failed to update status: ${err.response?.data?.message || err.message}`);
            })
            .finally(() => {
                hideLoader();
            });
    };

    // Fixed export function to export filtered data
    const handleExport = () => {
        const exportData = filteredSettlements.length > 0 ? filteredSettlements : settlements;

        if (exportData.length === 0) {
            alert("There is no data to export.");
            return;
        }

        const escapeCsvField = (field) => {
            const str = String(field ?? '');
            if (str.includes(',') || str.includes('"') || str.includes('\n')) {
                return `"${str.replace(/"/g, '""')}"`;
            }
            return str;
        };

        const headers = [
            "S.NO",
            "Vendor ID",
            "Vendor Name",
            "Total Sales",
            "Service Charges",
            "Commission Charges",
            "To Settle",
            "Total Cash",
            "From Date",
            "To Date",
            "Admin Account",
            "Vendor Account",
            "IFSC Code",
            "Settlement Date",
            "Status",
            "Payment Mode",
            "Remarks"
        ];

        const csvRows = exportData.map((item) => [
            item.sno || '',
            item.vendorId || '',
            item.holderName || '',
            item.totalAmountBeforeTds || 0,
            item.tdsAmount || 0,
            item.commission || 0,
            item.finalAmount || 0,
            item.totalCash || 0,
            formatDate(item.fromDate),
            formatDate(item.toDate),
            item.superadminAccNo || '',
            item.accountNumber || '',
            item.ifscCode || '',
            item.settlementDate || '',
            item.paymentStatus || '',
            item.pytMode || 'FT',
            item.remarks || ''
        ]);

        const csvString = [
            headers.map(escapeCsvField).join(','),
            ...csvRows.map(row => row.map(escapeCsvField).join(','))
        ].join('\r\n');

        const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", "settlements.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    // Get status badge style
    const getStatusStyle = (status) => {
        const baseStyle = {
            padding: "4px 8px",
            borderRadius: "12px",
            fontSize: "12px",
            fontWeight: "500",
            display: "inline-block",
            textTransform: "uppercase"
        };

        switch (status) {
            case "PAID":
                return {
                    ...baseStyle,
                    backgroundColor: "#4CAF50",
                    color: "white"
                };
            case "PENDING":
                return {
                    ...baseStyle,
                    backgroundColor: "#FF9800",
                    color: "white"
                };
            default:
                return {
                    ...baseStyle,
                    backgroundColor: "#9E9E9E",
                    color: "white"
                };
        }
    };

    // Get payment mode badge style
    const getPaymentModeStyle = (mode) => {
        const baseStyle = {
            padding: "4px 8px",
            borderRadius: "4px",
            fontSize: "12px",
            fontWeight: "500",
            display: 'inline-block'
        };

        switch (mode) {
            case "FT":
                return {
                    ...baseStyle,
                    backgroundColor: "#2196F3",
                    color: "white"
                };
            case "NEFT":
                return {
                    ...baseStyle,
                    backgroundColor: "#4CAF50",
                    color: "white"
                };
            case "RTGS":
                return {
                    ...baseStyle,
                    backgroundColor: "#9C27B0",
                    color: "white"
                };
            case "IMPS":
                return {
                    ...baseStyle,
                    backgroundColor: "#FF9800",
                    color: "white"
                };
            default:
                return {
                    ...baseStyle,
                    backgroundColor: "#9E9E9E",
                    color: "white"
                };
        }
    };

    // Format date to display only date part
    const formatDate = (dateString) => {
        if (!dateString) return "-";
        const date = new Date(dateString);
        return date.toISOString().split("T")[0];
    };

    const tableHeaderStyle = {
        border: "1px solid #ddd",
        textAlign: "center",
        fontWeight: "bold",
        backgroundColor: "#b15cd6",
        color: "white",
        padding: "10px 8px",
        fontSize: "13px",
        whiteSpace: "nowrap",
        position: "sticky",
        top: 0,
        zIndex: 10,
    };
    const tableCellStyle = {
        border: "1px solid #ddd",
        textAlign: "center",
        padding: "10px 8px",
        fontSize: "13px",
        whiteSpace: "nowrap",
    };

    // Modal styles
    const modalOverlayStyle = {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 9999,
        padding: '20px',
    };

    const modalContentStyle = {
        backgroundColor: '#fff',
        borderRadius: '8px',
        width: '100%',
        maxWidth: '500px',
        maxHeight: '90vh',
        overflowY: 'auto',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
    };

    const modalHeaderStyle = {
        padding: '20px',
        borderBottom: '1px solid #eee',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    };

    const modalBodyStyle = {
        padding: '20px',
    };

    return (
        <div className="container-fluid">
            <div className="row" style={{ marginTop: "-122px" }}>
                <div className="col-lg-10 food-accounts-data-scroll" style={{ height: "85vh", overflowY: "auto", scrollbarWidth: "none" }}>
                    {/* Filters and Export Button - Same as Userdetails */}
                    <div className="row g-2 mb-1 align-items-center me-0">
                        {/* Search Type Dropdown and Search Input - Side by Side */}
                        <div className="col-md-auto col-12">
                            <div className="d-flex align-items-center" style={{ gap: '10px' }}>
                                {/* Search Type Dropdown */}
                                <div style={{ minWidth: '150px' }}>
                                    <select
                                        className="form-select"
                                        value={searchType}
                                        onChange={(e) => setSearchType(e.target.value)}
                                        style={{ height: "40px" }}
                                    >
                                        {searchOptions.map(option => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {/* Search Input */}
                                <div style={{ minWidth: '200px' }}>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder={getPlaceholderText()}
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        style={{ height: "40px" }}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Status Filter */}
                        <div className="col-md-auto col-12">
                            <select
                                className="form-select"
                                value={selectedStatusFilter}
                                onChange={(e) => setSelectedStatusFilter(e.target.value)}
                                aria-label="Filter by status"
                                style={{ maxWidth: '150px', height: "40px" }}
                            >
                                <option value="">All Status</option>
                                {uniqueStatuses.map(status => (
                                    <option key={status} value={status}>{status}</option>
                                ))}
                            </select>
                        </div>

                        {/* Date Filter */}
                        <div className="col-md-auto col-12">
                            <input
                                className="form-control"
                                type="date"
                                value={selectedDate}
                                onChange={(e) => setSelectedDate(e.target.value)}
                                style={{ height: "40px", minWidth: '150px' }}
                            />
                        </div>

                        {/* Clear Filters Button */}
                        <div className="col-md-auto col-12">
                            <button
                                className="btn"
                                onClick={clearAllFilters}
                                style={{
                                    backgroundColor: '#6c757d',
                                    color: '#fff',
                                    height: "40px",
                                    padding: "0 15px"
                                }}
                            >
                                Clear Filters
                            </button>
                        </div>

                        {/* Export Button */}
                        <div className="col-md col-12">
                            <div className="d-flex justify-content-start justify-content-md-end">
                                <button
                                    onClick={handleExport}
                                    className="btn"
                                    style={{
                                        backgroundColor: '#b15cd6',
                                        color: '#fff',
                                        height: "40px",
                                        padding: "0 20px",
                                        fontWeight: "bold",
                                    }}
                                >
                                    Export CSV
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Table Container with Horizontal Scroll */}
                    <div style={{
                        width: "100%",
                        overflowX: "auto",
                        border: "1px solid #ddd",
                        borderRadius: "4px",
                        maxHeight: "70vh",
                        position: "relative",
                        marginTop: "10px"
                    }}>
                        {/* Table */}
                        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "1500px" }}>
                            <thead>
                                <tr>
                                    <th style={tableHeaderStyle}>S.NO</th>
                                    <th style={tableHeaderStyle}>Vendor ID</th>
                                    <th style={tableHeaderStyle}>Total Sales</th>
                                    <th style={tableHeaderStyle}>Service Charges</th>
                                    <th style={tableHeaderStyle}>Commission Charges</th>
                                    <th style={tableHeaderStyle}>To Settle</th>
                                    <th style={tableHeaderStyle}>Total Cash</th>
                                    <th style={tableHeaderStyle}>From Date</th>
                                    <th style={tableHeaderStyle}>To Date</th>
                                    <th style={tableHeaderStyle}>Admin Acc</th>
                                    <th style={tableHeaderStyle}>Vendor Name</th>
                                    <th style={tableHeaderStyle}>Vendor Acc</th>
                                    <th style={tableHeaderStyle}>IFSC</th>
                                    <th style={tableHeaderStyle}>Settlement Date</th>
                                    <th style={tableHeaderStyle}>Status</th>
                                    <th style={tableHeaderStyle}>Remarks</th>
                                    <th style={tableHeaderStyle}>Payment Mode</th>
                                    <th style={tableHeaderStyle}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredSettlements.length > 0 ? (
                                    filteredSettlements.map(item => (
                                        <tr key={item.settlementId || item.vendorId}>
                                            <td style={tableCellStyle}>{item.sno || '-'}</td>
                                            <td style={tableCellStyle}>{item.vendorId}</td>
                                            <td style={tableCellStyle}>₹{item.totalAmountBeforeTds}</td>
                                            <td style={tableCellStyle}>
                                                {item.tdsAmount > 0 ? `₹${item.tdsAmount}` : "₹0"}
                                            </td>
                                            <td style={tableCellStyle}>
                                                {item.commission > 0 ? `₹${item.commission}` : "₹0"}
                                            </td>
                                            <td style={tableCellStyle}>₹{item.finalAmount}</td>
                                            <td style={tableCellStyle}>₹{item.totalCash}</td>
                                            <td style={tableCellStyle}>{formatDate(item.fromDate)}</td>
                                            <td style={tableCellStyle}>{formatDate(item.toDate)}</td>
                                            <td style={tableCellStyle}>{item.superadminAccNo}</td>
                                            <td style={tableCellStyle}>{item.holderName}</td>
                                            <td style={tableCellStyle}>{item.accountNumber}</td>
                                            <td style={tableCellStyle}>{item.ifscCode}</td>
                                            <td style={tableCellStyle}>{item.settlementDate}</td>
                                            <td style={tableCellStyle}>
                                                <span style={getStatusStyle(item.paymentStatus)}>
                                                    {item.paymentStatus}
                                                </span>
                                            </td>
                                            <td style={tableCellStyle}>
                                                {item.remarks ? (
                                                    <div style={{
                                                        maxWidth: '200px',
                                                        whiteSpace: 'nowrap',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        cursor: 'pointer'
                                                    }}
                                                        title={item.remarks}
                                                        onClick={() => alert(`Remarks: ${item.remarks}`)}
                                                    >
                                                        {item.remarks}
                                                    </div>
                                                ) : "-"}
                                            </td>
                                            <td style={tableCellStyle}>
                                                <span style={getPaymentModeStyle(item.pytMode)}>
                                                    {item.pytMode || "FT"}
                                                </span>
                                            </td>
                                            <td style={tableCellStyle}>
                                                <button
                                                    onClick={() => handleStatusUpdateClick(item)}
                                                    style={{
                                                        padding: "4px 8px",
                                                        backgroundColor: "#2196F3",
                                                        color: "white",
                                                        border: "none",
                                                        borderRadius: "4px",
                                                        cursor: "pointer",
                                                        fontSize: "12px"
                                                    }}
                                                >
                                                    Update Status
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="18" style={{
                                            fontStyle: "italic",
                                            textAlign: "center",
                                            padding: "20px",
                                            color: "#888"
                                        }}>
                                            {settlements.length === 0
                                                ? "Loading data or no settlements found."
                                                : (searchQuery.trim() || selectedStatusFilter || selectedDate)
                                                    ? "No settlements found with the specified filters."
                                                    : "No settlements match the current filters."}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Status Update Modal */}
                    {showStatusModal && selectedSettlement && (
                        <div style={modalOverlayStyle} onClick={(e) => e.target === e.currentTarget && setShowStatusModal(false)}>
                            <div style={modalContentStyle}>
                                <div style={modalHeaderStyle}>
                                    <h3 style={{ margin: 0, color: '#333' }}>
                                        Update Settlement Status
                                    </h3>
                                    <button
                                        onClick={() => setShowStatusModal(false)}
                                        style={{
                                            background: 'transparent',
                                            border: 'none',
                                            fontSize: '20px',
                                            cursor: 'pointer',
                                            color: '#666',
                                            fontWeight: 'bold'
                                        }}
                                    >
                                        ✕
                                    </button>
                                </div>

                                <div style={modalBodyStyle}>
                                    <div style={{ marginBottom: '15px' }}>
                                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                                            Settlement ID
                                        </label>
                                        <input
                                            type="text"
                                            value={selectedSettlement.settlementId}
                                            readOnly
                                            style={{
                                                width: '100%',
                                                padding: '8px',
                                                border: '1px solid #ddd',
                                                borderRadius: '4px',
                                                backgroundColor: '#f5f5f5',
                                                cursor: 'not-allowed'
                                            }}
                                        />
                                    </div>

                                    <div style={{ marginBottom: '15px' }}>
                                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                                            Vendor ID
                                        </label>
                                        <input
                                            type="text"
                                            value={selectedSettlement.vendorId}
                                            readOnly
                                            style={{
                                                width: '100%',
                                                padding: '8px',
                                                border: '1px solid #ddd',
                                                borderRadius: '4px',
                                                backgroundColor: '#f5f5f5',
                                                cursor: 'not-allowed'
                                            }}
                                        />
                                    </div>

                                    <div style={{ marginBottom: '15px' }}>
                                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                                            Vendor Name
                                        </label>
                                        <input
                                            type="text"
                                            value={selectedSettlement.holderName || "-"}
                                            readOnly
                                            style={{
                                                width: '100%',
                                                padding: '8px',
                                                border: '1px solid #ddd',
                                                borderRadius: '4px',
                                                backgroundColor: '#f5f5f5',
                                                cursor: 'not-allowed'
                                            }}
                                        />
                                    </div>

                                    <div style={{ marginBottom: '15px' }}>
                                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                                            Current Status
                                        </label>
                                        <div style={getStatusStyle(selectedSettlement.paymentStatus)}>
                                            {selectedSettlement.paymentStatus}
                                        </div>
                                    </div>

                                    <div style={{ marginBottom: '15px' }}>
                                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                                            Payment Mode
                                        </label>
                                        <select
                                            value={pytMode}
                                            onChange={(e) => setPytMode(e.target.value)}
                                            style={{
                                                width: '100%',
                                                padding: '8px',
                                                border: '1px solid #ddd',
                                                borderRadius: '4px'
                                            }}
                                        >
                                            <option value="FT">FT (Fund Transfer)</option>
                                            <option value="NEFT">NEFT</option>
                                            <option value="RTGS">RTGS</option>
                                            <option value="IMPS">IMPS</option>
                                        </select>
                                    </div>

                                    <div style={{ marginBottom: '15px' }}>
                                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                                            New Status *
                                        </label>
                                        <select
                                            value={newStatus}
                                            onChange={(e) => setNewStatus(e.target.value)}
                                            style={{
                                                width: '100%',
                                                padding: '8px',
                                                border: '1px solid #ddd',
                                                borderRadius: '4px'
                                            }}
                                            required
                                        >
                                            <option value="">Select Status</option>
                                            <option value="PENDING">PENDING</option>
                                            <option value="PAID">PAID</option>
                                        </select>
                                    </div>

                                    <div style={{ marginBottom: '20px' }}>
                                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                                            Remarks *
                                        </label>
                                        <textarea
                                            value={remarks}
                                            onChange={(e) => setRemarks(e.target.value)}
                                            placeholder="Enter remarks for status update (required)"
                                            rows="3"
                                            style={{
                                                width: '100%',
                                                padding: '8px',
                                                border: '1px solid #ddd',
                                                borderRadius: '4px',
                                                resize: 'vertical'
                                            }}
                                            required
                                        />
                                        <small style={{ color: '#666', fontSize: '12px', marginTop: '5px', display: 'block' }}>
                                            Please provide a reason for the status change
                                        </small>
                                    </div>

                                    <div style={{ display: 'flex', gap: '10px' }}>
                                        <button
                                            onClick={handleUpdateStatus}
                                            style={{
                                                flex: 1,
                                                padding: '10px',
                                                backgroundColor: '#4CAF50',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '4px',
                                                cursor: 'pointer',
                                                fontWeight: 'bold'
                                            }}
                                        >
                                            Update Status
                                        </button>
                                        <button
                                            onClick={() => setShowStatusModal(false)}
                                            style={{
                                                flex: 1,
                                                padding: '10px',
                                                backgroundColor: '#f5f5f5',
                                                color: '#333',
                                                border: '1px solid #ddd',
                                                borderRadius: '4px',
                                                cursor: 'pointer',
                                                fontWeight: 'bold'
                                            }}
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Eventsettlementpage;